import { useEffect, useMemo, useState } from "react";
import { Trash2, ArrowRight, Upload, Activity } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function RedirectsPanel() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    return api
      .get("/redirects")
      .then((r) => setRules(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const addSingle = async (e) => {
    e.preventDefault();
    if (!from.trim() || !to.trim()) {
      toast.error("Both From and To are required");
      return;
    }
    try {
      await api.post("/redirects", { from_path: from.trim(), to_path: to.trim() });
      toast.success("Redirect added");
      setFrom("");
      setTo("");
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Add failed");
    }
  };

  const importBulk = async () => {
    if (!bulkText.trim()) {
      toast.error("Paste at least one line first");
      return;
    }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const { data } = await api.post("/redirects/bulk", { text: bulkText });
      setBulkResult(data);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated, ${data.skipped?.length || 0} skipped`);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const del = async (rid) => {
    if (!window.confirm("Delete this redirect?")) return;
    try {
      await api.delete(`/redirects/${rid}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter(
      (r) => r.from_path.toLowerCase().includes(q) || r.to_path.toLowerCase().includes(q)
    );
  }, [rules, search]);

  const totalHits = rules.reduce((s, r) => s + (r.hits || 0), 0);

  return (
    <div className="space-y-5" data-testid="admin-redirects-panel">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="brut bg-white p-4">
          <div className="font-mono uppercase text-[10px] tracking-widest opacity-70">
            Total redirects
          </div>
          <div className="font-display text-3xl mt-1">{rules.length}</div>
        </div>
        <div className="brut bg-white p-4">
          <div className="font-mono uppercase text-[10px] tracking-widest opacity-70 inline-flex items-center gap-1">
            <Activity className="w-3 h-3" strokeWidth={3} />
            Total hits
          </div>
          <div className="font-display text-3xl mt-1">{totalHits}</div>
        </div>
        <div className="brut pcs-bg-cream p-4 col-span-2 md:col-span-1">
          <div className="font-mono uppercase text-[10px] tracking-widest opacity-70">
            How it works
          </div>
          <div className="text-xs mt-1 leading-snug">
            Stale URLs (e.g. /2011/best-vampire-costumes) auto-forward to the new
            equivalent via a client-side redirect — preserves traffic from old
            backlinks.
          </div>
        </div>
      </div>

      {/* Bulk import */}
      <div className="brut bg-white p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Upload className="w-4 h-4" strokeWidth={2.5} />
          <div className="font-display text-2xl tracking-tight">Bulk import</div>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest opacity-60">
            One per line · /old → /new
          </span>
        </div>
        <textarea
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          rows={8}
          placeholder={`/2011/best-vampire-costumes -> /category/halloween\n/old-product/some-thing -> /product/new-thing\n# comments and blank lines are ignored\n/contact -> /\n`}
          data-testid="redirects-bulk-input"
          className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono text-xs resize-y"
        />
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">
            Supports: <code className="bg-gray-100 px-1">/old -&gt; /new</code>,
            comma, or tab between old and new.
          </div>
          <button
            type="button"
            onClick={importBulk}
            disabled={bulkBusy || !bulkText.trim()}
            data-testid="redirects-bulk-import"
            className="brut pcs-bg-pink text-white px-4 py-2 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
          >
            {bulkBusy ? "Importing..." : "Import all"}
          </button>
        </div>
        {bulkResult && (
          <div className="brut pcs-bg-cream p-3 text-xs font-mono space-y-1" data-testid="redirects-bulk-result">
            <div>
              <strong>{bulkResult.created}</strong> created · <strong>{bulkResult.updated}</strong> updated ·{" "}
              <strong>{bulkResult.skipped?.length || 0}</strong> skipped (of{" "}
              {bulkResult.total_lines} lines)
            </div>
            {bulkResult.skipped?.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer">Skipped lines ↓</summary>
                <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                  {bulkResult.skipped.slice(0, 50).map((s, i) => (
                    <li key={`${i}-${s.line}`} className="opacity-80">
                      <span className="opacity-60">{s.reason}:</span> {s.line}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Single add */}
      <form
        onSubmit={addSingle}
        className="brut bg-white p-5 grid md:grid-cols-12 gap-3 items-end"
        data-testid="redirects-single-form"
      >
        <div className="md:col-span-12 font-display text-xl tracking-tight">Add one</div>
        <div className="md:col-span-5">
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            From path
          </label>
          <input
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            placeholder="/2011/best-vampire-costumes"
            data-testid="redirects-from-input"
            className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono text-xs"
          />
        </div>
        <div className="md:col-span-1 text-center text-2xl opacity-50 pb-1">→</div>
        <div className="md:col-span-5">
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            To path
          </label>
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="/category/halloween"
            data-testid="redirects-to-input"
            className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono text-xs"
          />
        </div>
        <div className="md:col-span-1">
          <button
            type="submit"
            data-testid="redirects-add-btn"
            className="w-full brut pcs-bg-pink text-white px-3 py-2 font-bold uppercase text-xs tracking-wide brut-press"
          >
            Add
          </button>
        </div>
      </form>

      {/* List */}
      <div className="brut bg-white">
        <div className="flex items-center gap-3 p-4 border-b-2 border-black">
          <div className="font-display text-xl tracking-tight">All redirects</div>
          <span className="font-mono text-[10px] uppercase tracking-widest opacity-60">
            ({filtered.length}{search ? ` of ${rules.length}` : ""})
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            data-testid="redirects-search"
            className="ml-auto brut bg-white px-3 py-1.5 text-xs focus:outline-none w-full max-w-xs"
          />
        </div>
        {loading ? (
          <div className="p-6 font-mono uppercase text-xs tracking-widest opacity-70">
            Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-700">
            {rules.length === 0
              ? "No redirects yet. Bulk-import your legacy URL map above."
              : "Nothing matches your search."}
          </div>
        ) : (
          <ul className="divide-y-2 divide-black/10">
            {filtered.map((r) => (
              <li
                key={r.id}
                className="p-3 grid grid-cols-[1fr_auto_1fr_auto_auto] items-center gap-2"
                data-testid={`redirect-row-${r.id}`}
              >
                <code className="font-mono text-xs truncate" title={r.from_path}>
                  {r.from_path}
                </code>
                <ArrowRight className="w-3 h-3 opacity-50" />
                <code className="font-mono text-xs truncate pcs-text-pink" title={r.to_path}>
                  {r.to_path}
                </code>
                <span
                  className="font-mono text-[10px] uppercase tracking-widest opacity-70 whitespace-nowrap"
                  title="Times this redirect has been served"
                >
                  {r.hits || 0} hits
                </span>
                <button
                  type="button"
                  onClick={() => del(r.id)}
                  data-testid={`redirect-delete-${r.id}`}
                  className="brut bg-white p-1.5 brut-press"
                  aria-label="Delete redirect"
                >
                  <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
